import type {
  AccountingKpis,
  DayOfWeek,
  HoursData,
  NetworkDashboardAvailableStore,
  NetworkDashboardCategories,
  NetworkDashboardHr,
  NetworkDashboardLiveStoreCa,
  NetworkDashboardLosses,
  NetworkDashboardSales,
  NetworkDashboardStoreAgeFilter,
  NetworkDashboardTopProducts,
  TimeSpentData,
} from '@gozoki/api-types'
import { type UseQueryResult, keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  type FetchNetworkDashboardKpisParams,
  fetchAccountingKpis,
  fetchAccountingWarnings,
  fetchHoursData,
  fetchNetworkDashboardAvailableStores,
  fetchNetworkDashboardCategories,
  fetchNetworkDashboardHr,
  fetchNetworkDashboardLiveStoreCa,
  fetchNetworkDashboardLosses,
  fetchNetworkDashboardSales,
  fetchNetworkDashboardTopProducts,
  fetchTimeSpentData,
} from '../queries/kpi.queries'
import { keys } from '../queries/keys'
import {
  networkDashboardSectionKey,
  normalizeNetworkDashboardSectionKeyParams,
} from '../queries/network-dashboard-query-keys'

export { networkDashboardSectionKey, normalizeNetworkDashboardSectionKeyParams }

export const useNetworkDashboardSales = (
  storeIds?: number[],
  storeAgeFilter: NetworkDashboardStoreAgeFilter = 'default',
  period?: FetchNetworkDashboardKpisParams,
  enabled = true,
): UseQueryResult<NetworkDashboardSales, unknown> =>
  useQuery({
    queryKey: networkDashboardSectionKey('sales', storeIds, storeAgeFilter, period),
    queryFn: ({ signal }) => fetchNetworkDashboardSales(storeIds, storeAgeFilter, period, signal),
    placeholderData: keepPreviousData,
    staleTime: 0,
    retry: false,
    enabled,
  })

export const useNetworkDashboardLosses = (
  storeIds?: number[],
  storeAgeFilter: NetworkDashboardStoreAgeFilter = 'default',
  period?: FetchNetworkDashboardKpisParams,
  enabled = true,
): UseQueryResult<NetworkDashboardLosses, unknown> =>
  useQuery({
    queryKey: networkDashboardSectionKey('losses', storeIds, storeAgeFilter, period),
    queryFn: ({ signal }) => fetchNetworkDashboardLosses(storeIds, storeAgeFilter, period, signal),
    placeholderData: keepPreviousData,
    staleTime: 0,
    retry: false,
    enabled,
  })

export const useNetworkDashboardCategories = (
  storeIds?: number[],
  storeAgeFilter: NetworkDashboardStoreAgeFilter = 'default',
  period?: FetchNetworkDashboardKpisParams,
  enabled = true,
): UseQueryResult<NetworkDashboardCategories, unknown> =>
  useQuery({
    queryKey: networkDashboardSectionKey('categories', storeIds, storeAgeFilter, period),
    queryFn: ({ signal }) =>
      fetchNetworkDashboardCategories(storeIds, storeAgeFilter, period, signal),
    placeholderData: keepPreviousData,
    staleTime: 0,
    retry: false,
    enabled,
  })

export const useNetworkDashboardTopProducts = (
  storeIds?: number[],
  storeAgeFilter: NetworkDashboardStoreAgeFilter = 'default',
  period?: FetchNetworkDashboardKpisParams,
  enabled = true,
): UseQueryResult<NetworkDashboardTopProducts, unknown> =>
  useQuery({
    queryKey: networkDashboardSectionKey('top-products', storeIds, storeAgeFilter, period),
    queryFn: ({ signal }) =>
      fetchNetworkDashboardTopProducts(storeIds, storeAgeFilter, period, signal),
    placeholderData: keepPreviousData,
    staleTime: 0,
    retry: false,
    enabled,
  })

export const useNetworkDashboardHr = (
  storeIds?: number[],
  storeAgeFilter: NetworkDashboardStoreAgeFilter = 'default',
  period?: FetchNetworkDashboardKpisParams,
  enabled = true,
): UseQueryResult<NetworkDashboardHr, unknown> =>
  useQuery({
    queryKey: networkDashboardSectionKey('hr', storeIds, storeAgeFilter, period),
    queryFn: ({ signal }) => fetchNetworkDashboardHr(storeIds, storeAgeFilter, period, signal),
    placeholderData: keepPreviousData,
    staleTime: 0,
    retry: false,
    enabled,
  })

// Liste indépendante de la période : la borne « Toujours » se calcule dessus AVANT de lancer la
// requête KPI, donc elle doit rester chaude entre deux navigations (le backend la cache 15 min).
export const useNetworkDashboardAvailableStores = (): UseQueryResult<
  NetworkDashboardAvailableStore[],
  unknown
> =>
  useQuery({
    queryKey: keys.networkDashboard.availableStores(),
    queryFn: () => fetchNetworkDashboardAvailableStores(),
    staleTime: 15 * 60 * 1000,
  })

export const useNetworkDashboardLiveStoreCa = (
  storeIds?: number[],
  storeAgeFilter: NetworkDashboardStoreAgeFilter = 'default',
  enabled = true,
): UseQueryResult<NetworkDashboardLiveStoreCa, unknown> =>
  useQuery({
    queryKey: keys.networkDashboard.live(
      [...(storeIds ?? [])].sort((left, right) => left - right),
      storeAgeFilter,
    ),
    queryFn: () => fetchNetworkDashboardLiveStoreCa(storeIds, storeAgeFilter),
    // 60s, in MILLISECONDS (react-query unit). The live CA endpoint runs a carts
    // aggregation + a write on every call; do NOT lower this (300 = 300ms = ~3 req/s).
    refetchInterval: 60_000,
    enabled,
  })

export const useAccountingKpis = (
  date: string,
  enabled = true,
): UseQueryResult<AccountingKpis, unknown> =>
  useQuery({
    queryKey: ['get-accounting-kpis', date],
    queryFn: () => fetchAccountingKpis(date),
    enabled,
  })

export const useAccountingWarnings = (
  storeIds?: number[],
  enabled = true,
): UseQueryResult<{ date: string; difference: number }[], unknown> => {
  const sortedStoreIds = [...(storeIds ?? [])].sort((left, right) => left - right)

  return useQuery({
    queryKey: ['get-accounting-warnings', sortedStoreIds],
    queryFn: () => fetchAccountingWarnings(sortedStoreIds),
    enabled,
  })
}

export const useHoursData = (
  days: DayOfWeek[],
  storeIds: number[],
  plage: 'AM' | 'PM' | 'DAY',
  dateStart: Date,
  dateEnd: Date,
  enabled = true,
): UseQueryResult<HoursData[], unknown> => {
  const plageKey = plage || 'DAY'

  return useQuery({
    queryFn: () => fetchHoursData(days, storeIds, plage, dateStart, dateEnd),
    queryKey: ['get-hours-data', days, storeIds, plageKey, dateStart, dateEnd],
    placeholderData: keepPreviousData,
    staleTime: 15 * 60 * 1000,
    enabled,
  })
}

export const useTimeSpentDatas = (
  days: DayOfWeek[],
  storeIds: number[],
  plage: 'AM' | 'PM' | 'DAY',
  dateStart: Date,
  dateEnd: Date,
  enabled = true,
): UseQueryResult<TimeSpentData[], unknown> => {
  const plageKey = plage || 'DAY'
  return useQuery({
    queryFn: () => fetchTimeSpentData(days, storeIds, plage, dateStart, dateEnd),
    queryKey: ['get-time-spent-data', days, storeIds, plageKey, dateStart, dateEnd],
    placeholderData: keepPreviousData,
    staleTime: 15 * 60 * 1000,
    enabled,
  })
}
